import { PitchType } from '~/apps/campsite-details/types'

export function tagSorter(photos, campsite, primaryPhoto, t) {
  const tags = {
    allPhotos: {},
    topLevel: {},
    secondLevel: {},
    pitchtypes: {},
    onsite: {},
    visitor: {},
  }

  const counts = {
    all: 0,
    pitchtypes: 0,
    onsite: 0,
    visitor: 0,
    topLevel: {},
  }

  const idToLabelMap = {
    tags: {
      'all-photos': '',
      'visitor-photos': '',
    },
    groups: {},
  }

  const labelToIdMap = {
    groups: {},
  }

  if (!photos) {
    return {
      tags,
      counts,
      idToLabelMap,
    }
  }

  const { photosById, tagsById, groupsById } = createLabelMaps(
    photos,
    idToLabelMap,
    labelToIdMap,
    campsite.pitchtypes,
  )

  const pitchtypesById = getBYOPitchtypes(photos, campsite.pitchtypes)
  const onSiteId = getOnSiteId(idToLabelMap, t)
  const onSiteAccomPhotos = getNonBYOPitchtypes(
    photos,
    campsite.pitchtypes,
    onSiteId,
  )
  const allPhotos = primaryPhoto
    ? addPrimary(photos.general, primaryPhoto)
    : photos.general
  sortPhotoTags(photos.general, tags)
  sortPhotoTags(photos.visitor, tags)
  for (const photos of Object.values(pitchtypesById)) {
    sortPhotoTags(photos, tags)
    // add the pitchtype photos to the all photos if they are not already there
    for (const photo of photos) {
      if (!allPhotos.some((p) => p.id === photo.id)) {
        allPhotos.push(photo)
      }
    }
  }
  // Remove On-site accommodation as we show this via non byo pitchtypes
  if (onSiteId) {
    delete tags.topLevel[onSiteId]
  }
  tags.pitchtypes = pitchtypesById
  tags.onsite = onSiteAccomPhotos
  tags.visitor['visitor-photos'] = photos.visitor
  if (allPhotos.length) {
    tags.allPhotos['all-photos'] = allPhotos
  }
  buildCounts(
    counts,
    tags,
    allPhotos,
    pitchtypesById,
    onSiteAccomPhotos,
    photos.visitor,
  )

  return {
    tags,
    counts,
    idToLabelMap,
    photosById,
    tagsById,
    groupsById,
  }
}

function addPrimary(generalPhotos, primaryPhoto) {
  const photos = generalPhotos.filter((photo) => {
    const primaryPhotoURL = primaryPhoto.url.masterImage.split('/')
    const currentPhoto = photo.url.masterImage.split('/')
    return (
      primaryPhotoURL[primaryPhotoURL.length - 1] !==
      currentPhoto[currentPhoto.length - 1]
    )
  })
  photos.unshift(primaryPhoto)
  return photos
}

function createLabelMaps(
  photos: { [key: string]: Array<Photo> },
  idToLabelMap: { [key: string]: { [key: string]: string } },
  labelToIdMap: { [key: string]: { [key: string]: string } },
  pitchtypes: Array<PitchType>,
) {
  const photosById: Record<number, Photo> = {}
  const tagsById: Record<number, Tag> = {}
  const groupsById: Record<number, Group> = {}
  Object.entries(photos)
    .flatMap(([key, value]) => {
      if (key === 'general') {
        return [...value]
      } else if (key === 'pitchtypes') {
        return Object.values(value).flat()
      } else {
        return []
      }
    })
    .forEach((photo) => {
      photosById[photo.photo_id] = photo
      photo.tags.forEach((tag) => {
        const { id, name, group } = tag
        tagsById[id] = { id, name, ...tag }
        groupsById[group.id] = { ...group }
        idToLabelMap.tags[id] = name
        idToLabelMap.groups[group.id] = group.name
        labelToIdMap.groups[group.name] = group.id
      })
    })

  Object.entries(photos.pitchtypes).forEach(
    ([pitchtypeId, pitchtypePhotos]) => {
      const pt = pitchtypes?.find((pt) => pt.id === pitchtypeId)
      if (pt) {
        idToLabelMap.tags[pitchtypeId] = pt.name
        pitchtypePhotos.forEach((photo: Photo) => {
          photosById[photo.photo_id] = photo
        })
      }
    },
  )
  return { photosById, tagsById, groupsById }
}

function getOnSiteId(idToLabelMap, t) {
  const onSiteEntry = Object.entries(idToLabelMap.groups).find(
    ([_id, label]) => {
      return label === t('On-site accommodation')
    },
  )
  return onSiteEntry ? onSiteEntry[0] : undefined
}

function getBYOPitchtypes(photos, pitchtypes) {
  return Object.keys(photos.pitchtypes).reduce(
    (pitchtypesById, pitchtypeId) => {
      const pt = pitchtypes?.find((pt) => pt.id === pitchtypeId)
      if (pt && pt.isByo) {
        pitchtypesById[pt.id] = photos.pitchtypes[pitchtypeId]
      }
      return pitchtypesById
    },
    {},
  )
}

function getNonBYOPitchtypes(photos, pitchtypes, onSiteId) {
  return Object.keys(photos.pitchtypes).reduce(
    (pitchtypesById, pitchtypeId) => {
      const pt = pitchtypes?.find((pt) => pt.id === pitchtypeId)
      if (pt && !pt.isByo) {
        pitchtypesById[pt.id] = photos.pitchtypes[pitchtypeId].map((photo) => {
          if (onSiteId) {
            const tag = photo.tags.find(
              (tag) => tag.group.id === parseInt(onSiteId),
            )
            return {
              label: tag?.name || '',
              ...photo,
            }
          } else {
            return photo
          }
        })
      }
      return pitchtypesById
    },
    {},
  )
}

function sortPhotoTags(photos, tags) {
  photos.forEach((photo) => {
    photo.tags.forEach((tag) => {
      if (tag.id in tags.secondLevel) {
        if (!photoExists(tags.secondLevel[tag.id], photo)) {
          tags.secondLevel[tag.id].push(photo)
        }
      } else {
        tags.secondLevel[tag.id] = [photo]
      }

      if (tag.group.id in tags.topLevel) {
        if (!(tag.id in tags.topLevel[tag.group.id])) {
          tags.topLevel[tag.group.id][tag.id] = tags.secondLevel[tag.id]
        } else if (!photoExists(tags.topLevel[tag.group.id][tag.id], photo)) {
          tags.topLevel[tag.group.id][tag.id].push(photo)
        }
      } else {
        tags.topLevel[tag.group.id] = {
          [tag.id]: [photo],
        }
      }
    })
  })
}

function buildCounts(
  counts,
  tags,
  allPhotos,
  pitchtypesById: { [key: number]: Array<Photo> },
  onSiteAccomPhotos: { [key: number]: Array<Photo> },
  visitorPhotos,
) {
  counts.all = allPhotos.length
  counts.pitchtypes = Object.values(pitchtypesById).reduce((count, pt) => {
    return count + pt.length
  }, 0)

  counts.onsite = Object.values(onSiteAccomPhotos).reduce((count, pt) => {
    return count + pt.length
  }, 0)

  counts.visitor = visitorPhotos.length
  counts.topLevel = Object.fromEntries(
    Object.entries(tags.topLevel).map(([key, value]) => {
      const count = Object.values(value).reduce((total, item) => {
        return total + item.length
      }, 0)

      return [key, count]
    }),
  )
}

function photoExists(photos, photo) {
  return photos.some((p) => p.url.masterImage === photo.url.masterImage)
}

interface Photo {
  photo_id: number
  tags: Array<Tag>
  // ... other properties
}

interface Tag {
  id: number
  name: string
  // ... other properties
}

interface Group {
  id: number
  name: string
  // ... other properties
}
